import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import GoogleAuth from '../components/GoogleAuth';
import { isValidJWT, getApiUrl } from '../utils/api';
import { getConfig } from '../config';

// PKCE utilities — no external deps, uses Web Crypto API
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const codeVerifier = btoa(String.fromCharCode.apply(null, Array.from(verifierBytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const encoder = new TextEncoder();
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(codeVerifier));
  const codeChallenge = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return { codeVerifier, codeChallenge };
}

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleGoogleSuccess = async (credential: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Send the credential to your backend for verification
      const response = await fetch(`${getApiUrl()}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (response.ok) {
        const data = await response.json();

        // Validate token format before storing
        if (!data.token || !isValidJWT(data.token)) {
          console.error('Received invalid JWT token from server');
          setError('Authentication failed: Invalid token received');
          return;
        }

        // Store the validated JWT token
        localStorage.setItem('authToken', data.token);

        // If a Stripe top-up was interrupted by re-auth, resume the confirm flow.
        const pendingSessionId = sessionStorage.getItem('pending_topup_session_id');
        if (pendingSessionId) {
          const amount = sessionStorage.getItem('pending_topup_amount') || '';
          sessionStorage.removeItem('pending_topup_session_id');
          sessionStorage.removeItem('pending_topup_amount');
          const params = new URLSearchParams({
            topup_success: 'true',
            session_id: pendingSessionId,
            ...(amount ? { amount } : {}),
          });
          navigate(`/dashboard/billing?${params.toString()}`);
          return;
        }

        // Redirect to dashboard or home
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error: any) => {
    setError('Google authentication failed');
    console.error('Google Auth Error:', error);
  };

  const handleGitHubLogin = async () => {
    const clientId = getConfig().githubClientId;
    if (!clientId) {
      setError('GitHub OAuth is not configured');
      return;
    }

    const state = generateRandomString(16);
    const { codeVerifier, codeChallenge } = await generatePKCE();

    sessionStorage.setItem('github_oauth_state', state);
    sessionStorage.setItem('github_oauth_code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: `${window.location.origin}/login`,
      scope: 'read:user user:email',
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `https://github.com/login/oauth/authorize?${params.toString()}`;
  };

  // Handle GitHub OAuth callback
  const handleGitHubCallback = useCallback(async (code: string, state: string) => {
    setIsLoading(true);
    setError(null);

    // Clean URL params immediately
    setSearchParams({}, { replace: true });

    const savedState = sessionStorage.getItem('github_oauth_state');
    const codeVerifier = sessionStorage.getItem('github_oauth_code_verifier');

    // Clear sessionStorage regardless of outcome
    sessionStorage.removeItem('github_oauth_state');
    sessionStorage.removeItem('github_oauth_code_verifier');

    if (state !== savedState) {
      setError('Authentication failed: state mismatch');
      setIsLoading(false);
      return;
    }

    if (!codeVerifier) {
      setError('Authentication failed: missing code verifier');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/auth/github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, code_verifier: codeVerifier }),
      });

      if (response.ok) {
        const data = await response.json();

        if (!data.token || !isValidJWT(data.token)) {
          setError('Authentication failed: Invalid token received');
          return;
        }

        localStorage.setItem('authToken', data.token);

        const pendingSessionId = sessionStorage.getItem('pending_topup_session_id');
        if (pendingSessionId) {
          const amount = sessionStorage.getItem('pending_topup_amount') || '';
          sessionStorage.removeItem('pending_topup_session_id');
          sessionStorage.removeItem('pending_topup_amount');
          const params = new URLSearchParams({
            topup_success: 'true',
            session_id: pendingSessionId,
            ...(amount ? { amount } : {}),
          });
          navigate(`/dashboard/billing?${params.toString()}`);
          return;
        }

        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'GitHub authentication failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, setSearchParams]);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state) {
      handleGitHubCallback(code, state);
    }
  }, [searchParams, handleGitHubCallback]);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Navigation />
      <div className="flex-1 pt-20 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Sign In</h1>
              <p className="text-gray-400">
                Access your PropAlgos account to start building trading strategies
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-900/50 border border-red-800/50 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div className="flex flex-col items-center">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span className="ml-2 text-gray-400">Signing in...</span>
                  </div>
                ) : (
                  <div className="w-full max-w-sm">
                    <GoogleAuth
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                    />
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-800"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900 text-gray-400">or</span>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={handleGitHubLogin}
                  disabled={isLoading}
                  className="w-full max-w-sm bg-gray-800 hover:bg-gray-700 text-white py-3 px-4 rounded-lg transition-colors border border-gray-700 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  <span>Continue with GitHub</span>
                </button>
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors underline">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
